import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { FaMinusCircle, FaWrench } from "react-icons/fa";
import { FiEdit2, FiPackage } from "react-icons/fi";
import { MdError, MdPayments } from "react-icons/md";
import { RiBankFill, RiCalendarScheduleLine } from "react-icons/ri";
import { TbArrowBackUp } from "react-icons/tb";
import ChargebackBadge from "@/components/billing/chargeback-badge";
import { DiscountSourceBadge } from "@/components/billing/discount-source-badge";
import SoldByProviderDisplay from "@/components/billing/sold-by-provider-display";
import CollapsibleText from "@/components/ui/collapsible-text";
import IdDisplay from "@/components/ui/id-display";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Tooltip from "@/components/ui/tooltip";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { useProviders } from "@/hooks/use-providers";
import CardIcon from "@/lib/billing/get-card-icon";
import { getLastFourDigitsFromPaymentMethod } from "@/lib/billing/get-last-four-digits-from-payment-method";
import { discountSourceToType, formatReceiptDiscountAmount } from "@/lib/discount-manager";
import { calculateRelativeDuration, formatCurrency, formatDateTime, formatStaffName, formatToHumanReadable } from "@/lib/format-data";
import { parseError } from "@/lib/parse-error";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { formatAddressLine } from "@/lib/print-utils";
import { cx } from "@/lib/tailwind-utils";
import { useToast } from "@/lib/use-toast";
import { ChargeItem, ChargeTimelineItem, ShopShippingAddress } from "@/types/charge";
import { ChargeStatus } from "@/types/enums";
import { PaymentStatus } from "@/types/enums/payment";
import { PaymentMethod } from "@/types/payment";
import { chargeQueryOptions, useChargeMutations } from "@/api/provider/billing/charge-query";

// TODO: When the user makes a refund via the dropdown, the timeline should update to show the refund.

export function formatShippingAddress(address: ShopShippingAddress) {
  const line = formatAddressLine({
    street: address.address,
    addressLineTwo: address.addressLineTwo,
    city: address.city,
    state: address.state,
    zipCode: address.zipCode
  });
  return [address.name?.trim(), line].filter(Boolean).join(", ");
}
export function paymentDetails(paymentMethod: PaymentMethod) {
  const madeThrough = <span className="capitalize md:normal-case">
      made <span className="lowercase">with</span>
    </span>;
  if (paymentMethod?.brand) {
    return <>
        {madeThrough}
        <div className="ml-1.5 flex flex-row items-center text-caption">
          <CardIcon paymentMethod={paymentMethod} className="mr-1.5" size={26} />
          <span>•••• {getLastFourDigitsFromPaymentMethod(paymentMethod)}</span>
        </div>
      </>;
  } else if (paymentMethod?.bankName) {
    return <>
        {madeThrough}
        <div className="ml-1.5 flex flex-row items-center">
          <Tooltip content={paymentMethod.bankName}>
            <RiBankFill className="mr-1.5" size={26} />
          </Tooltip>
          <span className="text-caption text-foreground">
            •••• {paymentMethod.accountNumberLast4}
          </span>
        </div>
      </>;
  } else {
    return null;
  }
}
export function TimelineIcon(t0) {
  const $ = _c(12);
  const {
    item
  } = t0;
  switch (item.type) {
    case "CHARGE":
      {
        switch (item.status) {
          case ChargeStatus.PAID:
          case ChargeStatus.EXTERNAL_SETTLEMENT:
          case ChargeStatus.REFUNDED:
            {
              let t1;
              if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
                t1 = <div className="h-2.5 w-2.5 rounded-full bg-primary ring-4 ring-primary/10 brightness-125" aria-hidden={true} />;
                $[0] = t1;
              } else {
                t1 = $[0];
              }
              return t1;
            }
          case ChargeStatus.OUTSTANDING:
            {
              let t1;
              if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
                t1 = <div className="h-2.5 w-2.5 rounded-full bg-foreground-muted ring-4 ring-background" aria-hidden={true} />;
                $[1] = t1;
              } else {
                t1 = $[1];
              }
              return t1;
            }
          case ChargeStatus.VOID:
          case ChargeStatus.WRITE_OFF:
            {
              let t1;
              if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
                t1 = <FaMinusCircle className="h-4 w-4 text-foreground-muted" aria-hidden={true} />;
                $[2] = t1;
              } else {
                t1 = $[2];
              }
              return t1;
            }
          case ChargeStatus.CHARGEBACK:
            {
              let t1;
              if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
                t1 = <MdError className="h-4 w-4 text-status-error-text" aria-hidden={true} />;
                $[3] = t1;
              } else {
                t1 = $[3];
              }
              return t1;
            }
          default:
            {
              let t1;
              if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
                t1 = <div className="h-2.5 w-2.5 rounded-full bg-background-secondary ring-4 ring-background-secondary/50" aria-hidden={true} />;
                $[4] = t1;
              } else {
                t1 = $[4];
              }
              return t1;
            }
        }
      }
    case "PAYMENT":
    case "EXTERNAL_SETTLEMENT":
      {
        let t1;
        if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <MdPayments className="h-5 w-5 text-green-400" aria-hidden={true} />;
          $[5] = t1;
        } else {
          t1 = $[5];
        }
        return t1;
      }
    case "SCHEDULED_PAYMENT":
      {
        let t1;
        if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <RiCalendarScheduleLine className="h-4 w-4 text-foreground-muted" aria-hidden={true} />;
          $[6] = t1;
        } else {
          t1 = $[6];
        }
        return t1;
      }
    case "FAILED_PAYMENT":
      {
        let t1;
        if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <MdError className="h-4 w-4 text-status-error-text" aria-hidden={true} />;
          $[7] = t1;
        } else {
          t1 = $[7];
        }
        return t1;
      }
    case "ADJUSTMENT":
      {
        let t1;
        if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <FaWrench className="h-4 w-4 text-foreground-muted" aria-hidden={true} />;
          $[8] = t1;
        } else {
          t1 = $[8];
        }
        return t1;
      }
    case "CASH_DISCOUNT":
      {
        let t1;
        if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <FaMinusCircle className="h-4 w-4 text-foreground-muted" aria-hidden={true} />;
          $[9] = t1;
        } else {
          t1 = $[9];
        }
        return t1;
      }
    case "REFUND":
      {
        if (item.status === PaymentStatus.FAILED) {
          let t1;
          if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
            t1 = <MdError className="h-5 w-5 text-status-error-text" aria-hidden={true} />;
            $[10] = t1;
          } else {
            t1 = $[10];
          }
          return t1;
        }
        let t1;
        if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <TbArrowBackUp className="h-5 w-5 text-status-error-text" aria-hidden={true} />;
          $[11] = t1;
        } else {
          t1 = $[11];
        }
        return t1;
      }
    case "WRITE_OFF":
    default:
      {
        return null;
      }
  }
}
export function Dot() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="mx-1.5 text-lg">∙</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export default function ChargeTimeline(t0) {
  const $ = _c(47);
  const {
    chargeId,
    paymentId,
    actions
  } = t0;
  const {
    toast
  } = useToast();
  const {
    tenant
  } = useTenant();
  const {
    currentUser
  } = useUser();
  const tipsEnabled = tenant?.tipsEnabled !== false;
  const chargeMutations = useChargeMutations();
  const [isOpen, setIsOpen] = useState(false);
  const [editingTipId, setEditingTipId] = useState(null);
  let t1;
  if ($[0] !== chargeId || $[1] !== paymentId) {
    t1 = chargeQueryOptions.timeline(chargeId, paymentId);
    $[0] = chargeId;
    $[1] = paymentId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = {
      ...t1,
      retry: 1
    };
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: t3,
    isLoading: loading,
    error
  } = useQuery(t2);
  let t4;
  if ($[5] !== t3) {
    t4 = t3 === undefined ? [] : t3;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const items = t4;
  const {
    providers
  } = useProviders();
  let t5;
  if ($[7] !== currentUser) {
    t5 = hasPermission(currentUser, PERMISSIONS.BILLING_CHARGES_SOLD_BY_WRITE);
    $[7] = currentUser;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const canEditSoldBy = t5;
  let t6;
  if ($[9] !== chargeMutations || $[10] !== toast) {
    t6 = async (tipId, chargeId_0, providerId) => {
      ;
      try {
        await chargeMutations.updateTip.mutateAsync({
          tipId,
          chargeId: chargeId_0,
          tipUpdate: {
            providerId
          }
        });
        toast({
          title: "Tip updated",
          description: "The tip recipient has been updated successfully.",
          variant: "success"
        });
        setEditingTipId(null);
      } catch (t7) {
        const error_0 = t7;
        const errorMessage = await parseError(error_0);
        toast({
          title: "Failed to update tip",
          description: errorMessage,
          variant: "error"
        });
      }
    };
    $[9] = chargeMutations;
    $[10] = toast;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleUpdateTip = t6;
  let t7;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setTimeout(() => setIsOpen(true), 20);
    };
    t8 = [];
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[14] !== error || $[15] !== toast) {
    t9 = () => {
      if (error) {
        toast({
          title: "Failed to load timeline data",
          description: "Please try again later",
          variant: "error"
        });
      }
    };
    t10 = [error, toast];
    $[14] = error;
    $[15] = toast;
    $[16] = t10;
    $[17] = t9;
  } else {
    t10 = $[16];
    t9 = $[17];
  }
  useEffect(t9, t10);
  if (loading) {
    const t11 = isOpen ? "translate-y-0 opacity-100 duration-300" : "-translate-y-4 opacity-0 duration-200";
    let t12;
    if ($[18] !== t11) {
      t12 = cx("transform transition-all", t11);
      $[18] = t11;
      $[19] = t12;
    } else {
      t12 = $[19];
    }
    let t13;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <ul role="list" className="my-4 ml-4 space-y-6">{Array(2).fill(0).map(_temp)}</ul>;
      $[20] = t13;
    } else {
      t13 = $[20];
    }
    let t14;
    if ($[21] !== t12 || $[22] !== t13) {
      t14 = <div className={t12}>{t13}</div>;
      $[21] = t12;
      $[22] = t13;
      $[23] = t14;
    } else {
      t14 = $[23];
    }
    return t14;
  }
  const t11 = isOpen ? "translate-y-0 opacity-100 duration-300" : "-translate-y-4 opacity-0 duration-200";
  let t12;
  if ($[24] !== t11) {
    t12 = cx("transform transition-all", t11);
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== actions || $[27] !== canEditSoldBy || $[28] !== editingTipId || $[29] !== handleUpdateTip || $[30] !== items || $[31] !== providers || $[32] !== tipsEnabled) {
    let t14;
    if ($[34] !== actions || $[35] !== canEditSoldBy || $[36] !== editingTipId || $[37] !== handleUpdateTip || $[38] !== items.length || $[39] !== providers || $[40] !== tipsEnabled) {
      t14 = (item, ind) => <li key={item.id} className="relative flex gap-x-3"><div className={cx(ind === items.length - 1 ? "h-6" : "-bottom-6", "absolute left-0 top-0 flex w-6 justify-center")}><span className={cx("border-l", item.type === "SCHEDULED_PAYMENT" ? "border-dashed" : "border-solid")} aria-hidden={true} /></div><div className="flex w-full items-start space-x-2"><div className="relative flex h-6 w-6 flex-none items-center justify-center bg-background/70 backdrop-blur-sm rounded-full"><TimelineIcon item={item} /></div><div className="mb-1 flex w-full flex-col justify-center"><div className="flex items-start justify-between"><div className="min-w-0 flex-1"><div className="flex flex-col text-body font-medium text-foreground-secondary md:flex-row md:items-center"><span><span>{item.type === "CHARGE" && (item?.status === ChargeStatus.OUTSTANDING ? "Outstanding" : item?.status === ChargeStatus.CHARGEBACK ? "Chargeback on" : item?.status && [ChargeStatus.PAID, ChargeStatus.EXTERNAL_SETTLEMENT, ChargeStatus.REFUNDED].includes(item.status as ChargeStatus) ? "Paid" : "")}</span>{" "}<span>{item.type === "REFUND" && item.status === PaymentStatus.FAILED ? "Failed Refund" : formatToHumanReadable(item.type)}</span><span> </span>for{" "}{item.type === "ADJUSTMENT" ? item.amount > 0 ? <span>{formatCurrency(item.amount) + " less"}</span> : <span>{"an additional " + formatCurrency(-item.amount)}</span> : item.type === "CHARGE" && item.totalDiscount && item.totalDiscount > 0 ? <span><span className="mx-1 text-foreground-muted line-through">{formatCurrency(item.amount + item.totalDiscount)}</span><span>{formatCurrency(item.amount)}</span></span> : <span>{formatCurrency(item.amount)}</span>}{item.type === "CHARGE" && item.totalDiscount != null && item.totalDiscount > 0 && <span className="ml-1 text-caption text-green-600">(Discount: {formatCurrency(item.totalDiscount)})</span>}{item.type === "CHARGE" && item.items && item.items.length > 0 && (() => {
                      const totalTaxes = item.items.reduce(_temp2, 0);
                      return totalTaxes > 0 ? <span className="text-caption text-orange-600 md:ml-1">(incl. {formatCurrency(totalTaxes)} tax)</span> : null;
                    })()}</span>{item.type === "PAYMENT" && <>{item.paymentMethod && <div className="flex flex-row items-center md:ml-1">{paymentDetails(item.paymentMethod)}</div>}{item.status && item.status !== PaymentStatus.SUCCEEDED && <Tooltip content={item.status === PaymentStatus.FAILED ? item.failedReason || "Payment failed" : formatToHumanReadable(item.status)}><span className={cx("ml-1 rounded-md px-1.5 py-0.5 text-caption font-medium ring-1 ring-inset", item.status === PaymentStatus.FAILED ? "bg-red-50 text-red-900 ring-red-600/10 dark:bg-red-900/40 dark:text-red-400 dark:ring-red-400/20" : "bg-gray-100 text-gray-800 ring-gray-300/50 dark:bg-gray-600 dark:text-gray-100 dark:ring-gray-600/50")}>{formatToHumanReadable(item.status)}</span></Tooltip>}{item.chargebackTier && <ChargebackBadge tier={item.chargebackTier} dueDate={item.chargebackDueDate} className="ml-1 font-normal" />}</>}{item.type === "SCHEDULED_PAYMENT" && <>{item.paymentMethod && <div className="flex flex-row items-center md:ml-1">{paymentDetails(item.paymentMethod)}</div>}</>}</div>{item.paymentMedium && <p className="text-caption text-foreground-secondary">Made via{" "}<span>{["CASH", "CHECK", "CARE_CREDIT"].includes(item.paymentMedium) ? "external settlement" : item.paymentMedium === "POS" ? "POS" : formatToHumanReadable(item.paymentMedium)}</span></p>}{item.type === "PAYMENT" && item.feeToPatient && item.feeToPatient > 0 ? <p className="pt-0.5 text-caption text-foreground-secondary">Patient paid processing fee:{" "}{formatCurrency(item.feeToPatient)}</p> : null}<div className="flex items-center gap-x-2 text-caption text-foreground-secondary"><div className="flex items-center">{formatDateTime(item.date)} <Dot /><div className="-mt-1 flex items-center text-caption text-foreground-secondary">{calculateRelativeDuration(item.date)}{" "}</div></div></div><div className="flex items-center gap-x-2"><IdDisplay id={item.id} className="text-caption font-normal text-foreground-secondary" label={`${formatToHumanReadable(item.type)} ID`} /></div>{item.description && item.type !== "REFUND" && <p className="text-caption text-foreground-secondary">Description: {item.description}</p>}{item.shopShippingAddress && <IdDisplay id={formatShippingAddress(item.shopShippingAddress)} className="text-caption font-normal text-foreground-secondary" label="Ship to" truncate={false} alwaysShow={true} />}{item.comment && <p className="text-caption text-foreground-secondary">Comment: {item.comment}</p>}{item.type === "REFUND" && <>{item.status === PaymentStatus.FAILED && <div className="flex items-center text-caption font-medium text-status-error-text">Refund Failed</div>}{item.refundDestination === "PATIENT_CREDIT" && <div className="flex items-center text-caption text-foreground-secondary">To patient credit</div>}{(item.refundReason || item.description) && <div className="flex items-center text-caption text-foreground-secondary">{item.refundReason?.toUpperCase() === "OTHER" ? item.description || "Other" : item.refundReason ? `Reason: ${formatToHumanReadable(item.refundReason)}` : item.description}</div>}</>}{item.creator && <div className="flex flex-row items-center text-caption italic text-foreground-muted">Created by {formatStaffName(item.creator)}</div>}{item.type === "CHARGE" && item.tips && item.tips.length > 0 && <div className="flex flex-col gap-y-1 text-caption text-green-600">{item.tips.map(tip => {
                    const tipProvider = providers.find(p => p.id === tip.providerId);
                    const isEditing = editingTipId === tip.id;
                    return <div key={tip.id} className="flex flex-row items-center gap-x-2">{isEditing ? <><span>Tip to</span><Select value={tip.providerId} onValueChange={newProviderId => {
                          handleUpdateTip(tip.id, item.id, newProviderId);
                        }}><SelectTrigger className="h-6 w-auto min-w-[120px] text-caption"><SelectValue placeholder="Select provider" /></SelectTrigger><SelectContent>{providers.map(_temp3)}</SelectContent></Select><button onClick={() => setEditingTipId(null)} className="text-foreground-muted hover:text-foreground-secondary" title="Cancel">✕</button></> : <><span>Tip to{" "}<span>{tipProvider ? formatStaffName(tipProvider) : "Unknown Provider"}</span>{" - "}{formatCurrency(tip.amount)}</span>{tipsEnabled && <button onClick={() => setEditingTipId(tip.id)} className="text-foreground-muted transition-colors hover:text-foreground-secondary" title="Edit tip recipient"><FiEdit2 className="h-3 w-3" /></button>}</>}</div>;
                  })}{item.tips.length > 1 && <div className="flex flex-row items-center font-medium">Total Tips:{" "}{formatCurrency(item.tips.reduce(_temp4, 0))}</div>}</div>}</div>{ind === 0 && actions ? <div className="ml-4 flex shrink-0 items-center">{actions}</div> : null}</div>{item.items && item.items.length > 0 && <CollapsibleText text="Show items" collapsedText="Hide items" textClassName="text-caption"><h4 className="mt-1 text-caption font-medium text-foreground-secondary">Items</h4><ul className="mt-1 flex flex-col space-y-1">{item.items.map((chargeItem_0, chargeItemIndex) => {
                  const originalPrice = chargeItem_0.price;
                  const originalTotal = originalPrice * chargeItem_0.quantity;
                  const timelineDiscountTotal = chargeItem_0.discounts?.reduce(_temp5, 0) || 0;
                  const finalTotal = originalTotal - timelineDiscountTotal;
                  const hasItemDiscount = timelineDiscountTotal > 0;
                  const hasPackageSavings = !!chargeItem_0.soldPackageId;
                  return <li key={chargeItem_0.itemId || `custom-${chargeItem_0.name}-${chargeItemIndex}`} className="ml-4 flex flex-col gap-y-0.5 text-caption"><div className="flex flex-col gap-y-0.5"><div className="flex items-center gap-x-1"><span className="font-medium">{chargeItem_0.quantity}x</span><span className="flex items-center"><span>{chargeItem_0.name || chargeItem_0.item?.name}</span>{chargeItem_0.soldPackageId && <span className="ml-1 flex items-center gap-1 text-caption text-primary"><FiPackage className="h-3 w-3" />Package Pricing</span>}</span><span>@</span><span>{formatCurrency(originalPrice)}</span><span>=</span>{hasItemDiscount || hasPackageSavings ? <span className="flex items-center gap-1"><span className="text-foreground-muted line-through">{formatCurrency(originalTotal)}</span><span className={hasPackageSavings ? "text-primary" : "text-status-success-text"}>{formatCurrency(finalTotal)}</span></span> : <span>{formatCurrency(finalTotal)}</span>}</div>{chargeItem_0.discounts && chargeItem_0.discounts.length > 0 && <div className="space-y-0.5">{chargeItem_0.discounts.map(_temp6)}</div>}</div><div className="mt-1"><SoldByProviderDisplay items={[chargeItem_0 as ChargeItem]} readonly={!canEditSoldBy} inline={true} /></div></li>;
                })}</ul>{item.tips && item.tips.length > 0 && <><h4 className="mt-1 text-caption font-medium text-foreground-secondary">Tips</h4><ul className="mt-1 flex flex-col space-y-1">{item.tips.map(tip_1 => {
                    const tipProvider_0 = providers.find(p_0 => p_0.id === tip_1.providerId);
                    const isEditing_0 = editingTipId === tip_1.id;
                    return <li key={tip_1.id} className="ml-4 flex items-center justify-between gap-x-1 text-caption"><div className="flex items-center gap-x-2">{isEditing_0 ? <div className="flex items-center gap-x-2"><span className="font-medium text-green-600">Tip to</span><Select value={tip_1.providerId} onValueChange={newProviderId_0 => {
                            handleUpdateTip(tip_1.id, item.id, newProviderId_0);
                          }}><SelectTrigger className="h-6 w-auto min-w-[120px] text-caption"><SelectValue placeholder="Select provider" /></SelectTrigger><SelectContent>{providers.map(_temp7)}</SelectContent></Select><button onClick={() => setEditingTipId(null)} className="text-foreground-muted hover:text-foreground-secondary" title="Cancel">✕</button></div> : <><span className="font-medium text-green-600">Tip to{" "}<span>{tipProvider_0 ? formatStaffName(tipProvider_0) : "Provider"}</span></span>{tipsEnabled && <button onClick={() => setEditingTipId(tip_1.id)} className="text-foreground-muted transition-colors hover:text-foreground-secondary" title="Edit tip recipient"><FiEdit2 className="h-3 w-3" /></button>}</>}</div><span className="font-medium text-foreground-secondary">{formatCurrency(tip_1.amount)}</span></li>;
                  })}</ul><div className="ml-4 mt-1 text-caption font-medium text-foreground-secondary">Total Tips:{" "}{formatCurrency(item.tips.reduce(_temp8, 0))}</div></>}{item.items && item.items.length > 0 && item.items.some(_temp9) && <><h4 className="mt-1 text-caption font-medium text-foreground-secondary">Taxes</h4><ul className="mt-1 flex flex-col space-y-1">{item.items.map(_temp10)}</ul><div className="ml-4 mt-1 text-caption font-medium text-foreground-secondary">Total Taxes:{" "}{formatCurrency(item.items.reduce(_temp11, 0))}</div></>}</CollapsibleText>}</div></div></li>;
      $[34] = actions;
      $[35] = canEditSoldBy;
      $[36] = editingTipId;
      $[37] = handleUpdateTip;
      $[38] = items.length;
      $[39] = providers;
      $[40] = tipsEnabled;
      $[41] = t14;
    } else {
      t14 = $[41];
    }
    t13 = items.map(t14);
    $[26] = actions;
    $[27] = canEditSoldBy;
    $[28] = editingTipId;
    $[29] = handleUpdateTip;
    $[30] = items;
    $[31] = providers;
    $[32] = tipsEnabled;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[42] !== t13) {
    t14 = <ul role="list" className="my-4 ml-4 space-y-6">{t13}</ul>;
    $[42] = t13;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== t12 || $[45] !== t14) {
    t15 = <><div className={t12}>{t14}</div></>;
    $[44] = t12;
    $[45] = t14;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  return t15;
}
function _temp11(sum_3, chargeItem_2) {
  return sum_3 + (chargeItem_2.taxAmount || 0);
}
function _temp10(chargeItem_1, taxItemIndex) {
  if (!chargeItem_1.taxAmount || chargeItem_1.taxAmount <= 0) {
    return null;
  }
  return <li key={`${chargeItem_1.itemId || `custom-${chargeItem_1.name}-${taxItemIndex}`}-tax`} className="ml-4 flex items-center justify-between gap-x-1 text-caption"><div className="flex items-center gap-x-1"><span className="font-medium text-orange-600">Tax on{" "}<span>{chargeItem_1.name || chargeItem_1.item?.name}</span></span></div><span className="font-medium text-foreground-secondary">{formatCurrency(chargeItem_1.taxAmount)}</span></li>;
}
function _temp9(chargeItem_3) {
  return chargeItem_3.taxAmount && chargeItem_3.taxAmount > 0;
}
function _temp8(sum_2, tip_2) {
  return sum_2 + tip_2.amount;
}
function _temp7(provider_0) {
  return <SelectItem key={provider_0.id} value={provider_0.id}>{formatStaffName(provider_0)}</SelectItem>;
}
function _temp6(discount) {
  const discountType = discountSourceToType(discount.source);
  const quantityLabel = discount.quantity > 1 ? ` (x${discount.quantity})` : "";
  const detail = discount.sourceName ? `${discount.sourceName}${quantityLabel}` : quantityLabel.trim();
  return <div key={discount.id} className="flex items-center gap-1.5"><DiscountSourceBadge type={discountType} /><span className="text-caption text-status-success-text">{`${detail ? `${detail}: ` : ""}${formatReceiptDiscountAmount(discount.amount)}`}</span></div>;
}
function _temp5(sum_1, d) {
  return sum_1 + d.amount;
}
function _temp4(sum_0, tip_0) {
  return sum_0 + tip_0.amount;
}
function _temp3(provider) {
  return <SelectItem key={provider.id} value={provider.id}>{formatStaffName(provider)}</SelectItem>;
}
function _temp2(sum, chargeItem) {
  return sum + (chargeItem.taxAmount || 0);
}
function _temp(_, index) {
  return <li key={index} className="relative flex w-full items-start gap-x-3 space-x-2"><div className="relative flex h-6 w-6 flex-none items-center justify-center "><div className="h-5 w-5 animate-pulse rounded-full bg-background-secondary" /></div><div className="mb-1 flex flex-col justify-center"><div className="flex items-center"><div className="h-4 w-32 animate-pulse rounded bg-background-secondary" /><div className="ml-1 flex flex-row items-center"><div className="ml-1.5 h-4 w-40 animate-pulse rounded bg-background-secondary" /></div></div><div className="mt-1 h-3 w-24 animate-pulse rounded bg-background-secondary" /><div className="mt-1 flex items-center"><div className="h-3 w-32 animate-pulse rounded bg-background-secondary" /><div className="mx-1.5 h-3 w-3 animate-pulse rounded-full bg-background-secondary" /><div className="h-3 w-24 animate-pulse rounded bg-background-secondary" /></div><div className="mt-1 h-3 w-36 animate-pulse rounded bg-background-secondary" /></div></li>;
}